import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { formatDate, formatTime } from '@brimo/shared';
import { Banner, Button, Dialog, Pill, Skeleton, Textarea } from '../../ds/components';
import { ApiError, errorMessage } from '../../lib/api';
import { useWorkspace } from '../../lib/session';
import { FinancierAssignmentPanel } from '../financings/approval/FinancierAssignmentPanel';
import { GdprApprovalPanel } from '../gdpr/GdprApprovalPanel';
import { ReservationApprovalPanel } from '../reservations/ReservationApprovalPanel';
import {
  approveLabel,
  decisionLabel,
  useApproval,
  useDecideApproval,
  useWithdrawApproval,
  type ApprovalDetail,
} from './api';
import styles from './ApprovalDialog.module.css';

export interface ApprovalDialogProps {
  approvalId: string | null;
  onClose: () => void;
  onDecided: (approval: ApprovalDetail) => void;
}

const REASON_REQUIRED = 'Bitte geben Sie einen Grund an.';

/**
 * Detail of an approval (`?approval=<id>`): the request, its references, Genehmigen / Ablehnen with the
 * mandatory rejection reason, „Zurückziehen" for own requests; a request another module decides shows
 * that module's 409 text as a banner, a duplicate links to the merge screen.
 */
export function ApprovalDialog({ approvalId, onClose, onDecided }: ApprovalDialogProps) {
  const workspace = useWorkspace();
  const detail = useApproval(approvalId);
  const decide = useDecideApproval();
  const withdraw = useWithdrawApproval();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [blocked, setBlocked] = useState<string | null>(null);

  useEffect(() => {
    setRejecting(false);
    setReason('');
    setReasonError(null);
    setBlocked(null);
  }, [approvalId]);

  const approval = detail.data;
  const open = approvalId !== null;
  const own = approval?.requestedBy?.id === workspace.user.id;
  const pending = decide.isPending || withdraw.isPending;

  function submit(decision: 'approved' | 'rejected') {
    if (!approval) return;
    if (decision === 'rejected' && reason.trim() === '') {
      setReasonError(REASON_REQUIRED);
      return;
    }
    setReasonError(null);
    setBlocked(null);
    decide.mutate(
      { id: approval.id, body: { decision, reason: reason.trim() === '' ? null : reason.trim() } },
      {
        onSuccess: onDecided,
        onError: (error) => {
          if (error instanceof ApiError && error.status === 409) setBlocked(error.message);
        },
      },
    );
  }

  const serverFields = decide.error instanceof ApiError ? decide.error.fields : {};
  const genericError =
    (decide.isError && blocked === null && Object.keys(serverFields).length === 0
      ? errorMessage(decide.error)
      : null) ?? (withdraw.isError ? errorMessage(withdraw.error) : null);

  let footer = (
    <Button variant="secondary" onClick={onClose}>
      Schließen
    </Button>
  );
  // Reservation and release approvals are decided in the reservations module (Reservierung 08/09).
  const reservationPanel =
    approval !== undefined &&
    (approval.type === 'reservation' || approval.type === 'release') &&
    approval.refs.reservationId !== null;
  if (reservationPanel && approval) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        width={860}
        eyebrow={approval.typeLabel}
        title={approval.title}
        subtitle={approval.subtitle ?? undefined}
        footer={footer}
      >
        <ReservationApprovalPanel
          approval={approval}
          onDecided={() => void detail.refetch().then((fresh) => onDecided(fresh.data ?? approval))}
        />
      </Dialog>
    );
  }
  // Financier assignments are decided in the financings module (D4).
  if (approval && approval.type === 'financier_assignment' && approval.refs.financingId !== null) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        width={600}
        eyebrow={approval.typeLabel}
        title={approval.title}
        subtitle={approval.subtitle ?? undefined}
        footer={footer}
      >
        <FinancierAssignmentPanel
          approval={approval}
          onDecided={() => void detail.refetch().then((fresh) => onDecided(fresh.data ?? approval))}
        />
      </Dialog>
    );
  }
  // GDPR deletions show what the deletion removes and keep the state of the deletion job (M7).
  if (approval && approval.type === 'gdpr') {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        width={720}
        eyebrow={approval.typeLabel}
        title={approval.title}
        subtitle={approval.subtitle ?? undefined}
        footer={footer}
      >
        <GdprApprovalPanel
          approval={approval}
          onDecided={() => void detail.refetch().then((fresh) => onDecided(fresh.data ?? approval))}
        />
      </Dialog>
    );
  }
  if (approval && approval.status === 'open') {
    if (approval.decidable && !rejecting) {
      footer = (
        <>
          {own ? (
            <Button
              variant="ghost"
              disabled={pending}
              onClick={() => withdraw.mutate(approval.id, { onSuccess: onDecided })}
            >
              Zurückziehen
            </Button>
          ) : null}
          <Button variant="secondary" disabled={pending} onClick={() => setRejecting(true)}>
            Ablehnen
          </Button>
          <Button loading={decide.isPending} onClick={() => submit('approved')}>
            {approveLabel(approval.type)}
          </Button>
        </>
      );
    } else if (approval.decidable && rejecting) {
      footer = (
        <>
          <Button variant="secondary" disabled={pending} onClick={() => setRejecting(false)}>
            Abbrechen
          </Button>
          <Button variant="destructiveFilled" loading={decide.isPending} onClick={() => submit('rejected')}>
            Ablehnung bestätigen
          </Button>
        </>
      );
    } else if (own) {
      footer = (
        <>
          <Button variant="secondary" onClick={onClose}>
            Schließen
          </Button>
          <Button
            variant="destructive"
            loading={withdraw.isPending}
            onClick={() => withdraw.mutate(approval.id, { onSuccess: onDecided })}
          >
            Zurückziehen
          </Button>
        </>
      );
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width={600}
      eyebrow={approval ? approval.typeLabel : 'Freigabe'}
      title={approval ? approval.title : 'Freigabe'}
      subtitle={approval?.subtitle ?? undefined}
      footer={footer}
    >
      {detail.isError ? <Banner tone="danger">{errorMessage(detail.error)}</Banner> : null}
      {detail.isPending ? (
        <div className={styles.skeleton} aria-busy="true">
          <Skeleton width="60%" />
          <Skeleton width="45%" />
          <Skeleton width="70%" />
        </div>
      ) : null}
      {approval ? (
        <div className={styles.body}>
          {genericError ? <Banner tone="danger">{genericError}</Banner> : null}
          {blocked ? <Banner tone="info">{blocked}</Banner> : null}
          {approval.status === 'open' && !approval.decidable && approval.blockedReason ? (
            <Banner
              tone="info"
              actions={
                approval.type === 'duplicate' && approval.refs.duplicateCandidateId ? (
                  <Link
                    to={`/tasks/duplicates/${approval.refs.duplicateCandidateId}`}
                    className={styles.link}
                  >
                    Zusammenführung öffnen
                  </Link>
                ) : undefined
              }
            >
              {approval.blockedReason}
            </Banner>
          ) : null}
          {approval.status !== 'open' ? (
            <div className={styles.decision}>
              <Pill
                variant={
                  approval.status === 'approved'
                    ? 'success'
                    : approval.status === 'rejected'
                      ? 'danger'
                      : 'neutral'
                }
              >
                {decisionLabel(approval.status)}
              </Pill>
              <span className={styles.decisionMeta}>
                {approval.decidedBy ? `${approval.decidedBy.name} · ` : ''}
                {approval.decidedAt
                  ? `${formatDate(approval.decidedAt)} · ${formatTime(approval.decidedAt)}`
                  : ''}
              </span>
            </div>
          ) : null}
          <dl className={styles.rows}>
            <div className={styles.row}>
              <dt>Beantragt von</dt>
              <dd>{approval.requestedBy?.name ?? 'System'}</dd>
            </div>
            <div className={styles.row}>
              <dt>Beantragt am</dt>
              <dd>
                {formatDate(approval.requestedAt)} · {formatTime(approval.requestedAt)} · wartet seit{' '}
                {approval.waitingLabel}
              </dd>
            </div>
            <div className={styles.row}>
              <dt>Empfänger</dt>
              <dd>{approval.recipient.name}</dd>
            </div>
            {approval.message ? (
              <div className={styles.row}>
                <dt>Nachricht</dt>
                <dd>{approval.message}</dd>
              </div>
            ) : null}
            {approval.entity.contact ? (
              <div className={styles.row}>
                <dt>Kontakt</dt>
                <dd>
                  <Link to={approval.entity.contact.href}>{approval.entity.contact.name}</Link>
                </dd>
              </div>
            ) : null}
            {approval.entity.deal ? (
              <div className={styles.row}>
                <dt>Deal</dt>
                <dd>
                  <Link to={approval.entity.deal.href}>{approval.entity.deal.label}</Link>
                </dd>
              </div>
            ) : null}
            {approval.entity.financing ? (
              <div className={styles.row}>
                <dt>Finanzierung</dt>
                <dd>
                  <Link to={approval.entity.financing.href}>{approval.entity.financing.label}</Link>
                </dd>
              </div>
            ) : null}
            {approval.entity.property ? (
              <div className={styles.row}>
                <dt>Objekt</dt>
                <dd>
                  <Link to={approval.entity.property.href}>{approval.entity.property.name}</Link>
                </dd>
              </div>
            ) : null}
            {approval.entity.reservation ? (
              <div className={styles.row}>
                <dt>Reservierung</dt>
                <dd>
                  {approval.entity.reservation.unitNumber ?? '—'} · {approval.entity.reservation.status}
                  {approval.entity.reservation.signedAt
                    ? ` · unterschrieben ${formatDate(approval.entity.reservation.signedAt)}`
                    : ''}
                </dd>
              </div>
            ) : null}
            {approval.entity.duplicate ? (
              <div className={styles.row}>
                <dt>Doppeleintrag</dt>
                <dd>
                  <Link to={approval.entity.duplicate.contactA.href}>
                    {approval.entity.duplicate.contactA.name}
                  </Link>
                  {' · '}
                  <Link to={approval.entity.duplicate.contactB.href}>
                    {approval.entity.duplicate.contactB.name}
                  </Link>
                </dd>
              </div>
            ) : null}
            {approval.entity.proposedFinancier ? (
              <div className={styles.row}>
                <dt>Finanzierer</dt>
                <dd>{approval.entity.proposedFinancier.name}</dd>
              </div>
            ) : null}
            {approval.entity.tool ? (
              <div className={styles.row}>
                <dt>Tool</dt>
                <dd>{approval.entity.tool}</dd>
              </div>
            ) : null}
            {approval.reason ? (
              <div className={styles.row}>
                <dt>Grund</dt>
                <dd>{approval.reason}</dd>
              </div>
            ) : null}
            {approval.note ? (
              <div className={styles.row}>
                <dt>Notiz</dt>
                <dd>{approval.note}</dd>
              </div>
            ) : null}
          </dl>
          {rejecting ? (
            <Textarea
              label="Grund der Ablehnung"
              required
              rows={3}
              autoFocus
              value={reason}
              error={reasonError ?? serverFields['reason']}
              onChange={(event) => setReason(event.target.value)}
            />
          ) : null}
        </div>
      ) : null}
    </Dialog>
  );
}
